import { Role } from '@/types/Role'
import { Daycare } from '@/types/Daycare'
import { FunctionComponent, JSX, memo, NamedExoticComponent, useCallback, useState } from 'react'
import styles from './newsFeed.module.scss'
import Title from '@/components/_custom/Title/title'
import { Post, PostCategory } from '@/types/NewsFeed'
import PostRow, { PostRowProps } from './PostRow'
import PostDialog from './PostRow/Dialog/postDialog'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import { MoreHoriz } from '@mui/icons-material'
import DeletePostDialog from './Dialog/deletePostDialog'
import { useFeedData } from '@/hooks/useFeedData'
import SelectMultiplePills from '@/components/_custom/Selector/selectMultiplePills/selectMultiplePills'
import { useQuery } from '@apollo/client'
import { GET_POST_CATEGORIES } from '@/modules/API/news/news'
import { SelectOptionsDeprecated } from '@/types/Select'
import { useRefetchOnFocusOrOnline } from '@/hooks/useRefetchOnFocusOrOnline'

interface NewsFeedProps {
    role: Role
    daycare: Daycare
    canUpdate: boolean
    canValidPosts: boolean
    sectionIds: number[]
    childIds: number[]
    categoryIds: number[]
    setCategoryIds: (categoryIds: Array<number>) => void
    withoutSection: boolean
    isApplication: boolean
    setCountDraftPosts: (countDraftPosts: number) => void
}

const PostRowMemo: NamedExoticComponent<PostRowProps> = memo(PostRow, (prevProps, nextProps) => {
    return (
        prevProps.post.id === nextProps.post.id &&
        prevProps.post.updateDate === nextProps.post.updateDate &&
        prevProps.role === nextProps.role &&
        prevProps.daycareId === nextProps.daycareId &&
        prevProps.canUpdate === nextProps.canUpdate &&
        prevProps.canValidPosts === nextProps.canValidPosts &&
        prevProps.isApplication === nextProps.isApplication
    )
})

const NewsFeed: FunctionComponent<NewsFeedProps> = ({
    role,
    daycare,
    canUpdate,
    canValidPosts,
    sectionIds,
    childIds,
    withoutSection,
    isApplication,
    setCountDraftPosts,
    categoryIds,
    setCategoryIds
}: NewsFeedProps) => {
    const { t } = useTranslation()

    const [openDialogDelete, setOpenDialogDelete] = useState(false)
    const [deletePostId, setDeletePostId] = useState<number | null>(null)
    const [modifyPost, setModifyPost] = useState<Post | null>(null)
    const [openDialogPost, setOpenDialogPost] = useState(false)

    const { data: dataGetPostCategories } = useQuery(GET_POST_CATEGORIES, {
        variables: {
            daycareId: daycare.id
        }
    })

    const {
        sortedPosts,
        hasMorePosts,
        isLoading,
        isLoadingMore,
        isRefetching,
        error,
        hasData,
        loadedDaycareId,
        loadMore,
        refetch
    } = useFeedData({
        daycareId: daycare.id,
        role,
        isDraft: false,
        sectionIds,
        childIds,
        withoutSection,
        onDraftCountChange: setCountDraftPosts,
        categoryIds
    })

    useRefetchOnFocusOrOnline(refetch)

    const categoryOptions: SelectOptionsDeprecated = [
        {
            options: (dataGetPostCategories?.getPostCategories ?? []).map((category: PostCategory) => ({
                value: String(category.id),
                name: category.name
            }))
        }
    ]

    const handleModifyPost: (post: Post) => void = useCallback((post: Post): void => {
        setModifyPost(post)
        setOpenDialogPost(true)
    }, [])

    const handleDeletePost: (post: Post) => void = useCallback((post: Post): void => {
        if (post?.id) {
            setOpenDialogDelete(true)
            setDeletePostId(post.id)
        }
    }, [])

    const handleCloseDelete: () => void = useCallback((): void => {
        setOpenDialogDelete(false)
        setDeletePostId(null)
    }, [])

    const openPostPopUp: () => void = useCallback((): void => {
        setOpenDialogPost(true)
    }, [])

    const closePostPopUp: (doRefetch: boolean) => Promise<void> = useCallback(
        async (doRefetch: boolean): Promise<void> => {
            setOpenDialogPost(false)
            setModifyPost(null)
            if (doRefetch) await refetch()
        },
        [refetch]
    )

    const showAddButton: boolean = canUpdate && role !== Role.parent

    return (
        <div className={styles.NewsFeedPlacement}>
            <div className={styles.Form}>
                <div className={`${styles.TitleAndButton} ${showAddButton && styles.MarginBottom}`}>
                    <Title text={t('newsFeed')} className={styles.Title} />
                    {showAddButton && <ButtonTextIcon type={'add'} onClick={openPostPopUp} />}
                </div>

                {(isLoading || isRefetching) && <Loading overall={false} />}

                {categoryOptions && categoryOptions.length && (
                    <SelectMultiplePills
                        selectValues={categoryIds.map(String)}
                        setSelectValues={(values: Array<string>) => setCategoryIds(values.map(Number))}
                        selectOptions={categoryOptions}
                        isParent={role === Role.parent}
                        className={styles.MarginBottom}
                    />
                )}

                {hasData && loadedDaycareId === daycare.id && (
                    <div className={styles.Container}>
                        {sortedPosts.length === 0 ? (
                            <p className={styles.NoPostsText}>{t('noPosts')}</p>
                        ) : (
                            sortedPosts.map(
                                (post: Post): JSX.Element => (
                                    <PostRowMemo
                                        key={post.id}
                                        role={role}
                                        post={post}
                                        daycareId={daycare.id}
                                        handleModifyPost={handleModifyPost}
                                        handleDeletePost={handleDeletePost}
                                        canUpdate={canUpdate}
                                        canValidPosts={canValidPosts}
                                        isApplication={isApplication}
                                    />
                                )
                            )
                        )}

                        {isLoadingMore && <Loading overall={false} />}

                        {sortedPosts.length > 1 && hasMorePosts && (
                            <div className={styles.ButtonContainer}>
                                <ButtonTextIcon
                                    type={'custom'}
                                    className={styles.ShowMoreButton}
                                    customText={t('showMore')}
                                    customIcon={<MoreHoriz />}
                                    onClick={loadMore}
                                    parent={role === Role.parent}
                                />
                            </div>
                        )}

                        {openDialogPost && (
                            <PostDialog
                                open={openDialogPost}
                                handleClose={closePostPopUp}
                                role={role}
                                daycare={daycare}
                                modifyPost={modifyPost}
                                refetch={refetch}
                            />
                        )}
                    </div>
                )}
            </div>

            {error && <ErrorMessage error={t('generalError')} />}

            {openDialogDelete && deletePostId && (
                <DeletePostDialog
                    open={openDialogDelete}
                    handleClose={handleCloseDelete}
                    postId={deletePostId}
                    daycareId={daycare.id}
                    refetch={refetch}
                />
            )}
        </div>
    )
}

export default NewsFeed
